/**
 * Key–value pairs: account details, book details, build fingerprints.
 * items: [{ term, value, mono?, hint? }]. `layout`: "columns" (term left, value right; default) | "stacked".
 * Always stacked on phones. `mono` sets the value in the mono font (keys, codes, hashes).
 * (cockatrice-ui `src/ui/DescriptionList.jsx`, ported to hyperscript; PLAN.md §0)
 */
import './css/description-list.css';
import { mergeProps, splitProps } from 'solid-js';
import { each, el, when } from './solid.ts';
import type { Attributes, Child } from './solid.ts';

export interface DescriptionItem {
  /** Term. */
  readonly term: Child;
  /** Value. */
  readonly value: Child;
  /** Set the value in the mono font (keys, codes, hashes). */
  readonly mono?: boolean | undefined;
  /** Caption under the value. */
  readonly hint?: Child;
}
export interface DescriptionListProps extends Attributes {
  /** The pairs. */
  readonly items: readonly DescriptionItem[];
  /** Term beside or above the value. Default "columns". Always stacked on phones. */
  readonly layout?: 'columns' | 'stacked' | undefined;
  /** Hairlines between rows. Default true. */
  readonly divided?: boolean | undefined;
  /** Width of the term column. Default "200px". */
  readonly termWidth?: string | undefined;
  /** Extra classes. */
  readonly class?: string | undefined;
}

export default function DescriptionList(props: DescriptionListProps): Child {
  const [local, rest] = splitProps(props, ['class', 'items', 'layout', 'divided', 'termWidth']);
  return el(
    'dl',
    mergeProps({
      class: () =>
        ['ui-description-list', `ui-description-list--${local.layout ?? 'columns'}`, local.divided !== false && 'ui-description-list--divided', local.class]
          .filter(Boolean)
          .join(' '),
      style: () => (local.termWidth !== undefined && local.termWidth !== '' ? { '--ui-dl-term': local.termWidth } : undefined),
    }, rest),
    each(
      () => local.items,
      (it) =>
        el(
          'div',
          { class: 'ui-description-list__row' },
          el('dt', { class: 'ui-description-list__term' }, () => it.term),
          el(
            'dd',
            { class: () => ['ui-description-list__value', it.mono === true && 'ui-description-list__value--mono'].filter(Boolean).join(' ') },
            () => it.value,
            when(
              () => Boolean(it.hint),
              () => el('span', { class: 'ui-description-list__hint' }, () => it.hint),
            ),
          ),
        ),
    ),
  );
}
